<template>
  <div>
    <v-dialog
      :value="showCurrentInvite"
      @input="closeInviteModal"
      width="620"
      persistent
    >
      <modal-card :title="inviteTitle" padding @close="closeInviteModal">
        <div class="pt-4 pb-4 invite-text">
          You have been invited to the room <b>{{ currentInvite.roomName }}</b
          >. <br />To <i>leave</i> the current room and join the new room,
          please click join below. 👇🏽
        </div>

        <template v-slot:actions>
          <template v-if="isHost">
            <v-btn
              :href="inviteRoute()"
              target="_blank"
              text
              class="mr-4"
              @click="onHostLeave(false)"
            >
              Just Open, Don't Leave Yet
            </v-btn>
            <v-btn
              color="appMain"
              @click="onHostLeave(true)"
              :href="inviteRoute('join')"
              target="_blank"
            >
              Join
            </v-btn>
          </template>

          <v-btn
            v-else
            color="appMain"
            :href="inviteRoute('join')"
            target="_blank"
            >Join</v-btn
          >
        </template>
      </modal-card>
    </v-dialog>

    <v-dialog v-model="showInviteForm" width="420">
      <modal-card
        v-if="showInviteForm"
        title="Invite Info"
        padding
        @close="showInviteForm = false"
      >
        <div class="pt-2 pb-2">
          <v-alert
            type="warning"
            v-if="noSelectedParticipants"
            :icon="false"
            class="pa-3"
          >
            Please select at least one participant.
          </v-alert>

          <v-text-field
            label="Room Name"
            class="background-dark mb-3"
            v-model="inviteForm.roomName"
          />

          <hosts-select v-model="selectedHosts" :hostsList="filteredHosts" />
        </div>

        <template v-slot:actions>
          <v-btn
            :disabled="isLoading || noSelectedParticipants"
            :loading="isLoading"
            color="appMain"
            @click.prevent="inviteParticipants"
          >
            Send Invites
          </v-btn>
        </template>
      </modal-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex';

import GenericAction from './GenericAction';
import { eventBus } from '@/lib/events';
import { appRoute } from '@/globals';

import isEqual from 'lodash/isEqual';

import analyticSender from '@/components/rooms/helpers/analyticSender';
import { getAssignmentOrderMessage } from '@/components/rooms/helpers/assignmentNotification';

import HostsSelect from '@/components/rooms/actions/helpers/HostsSelect';

export default {
  name: 'ActionInvites',
  mixins: [GenericAction],
  components: {
    HostsSelect,
  },
  data() {
    return {
      isLoading: false,

      // controls for host
      showInviteForm: false,
      selectedHosts: [],
      inviteForm: {
        roomName: '',
      },

      // for rep
      showCurrentInvite: false,
      currentInvite: {
        from: {
          name: '',
          identity: null,
        },
        to: null,
        token: '',
      },
    };
  },

  computed: {
    inviteTitle() {
      if (this.currentInvite.from?.identity === this.roomInfo.identity) {
        return 'New Room Created';
      }

      return `New Invite from ${this.currentInvite.from?.name}`;
    },

    storageKey() {
      return `invite-dismissed-${this.roomInfo?.identity}`;
    },

    filteredHosts() {
      return this.hostParticipants.filter(({ identity }) => {
        return identity !== this.roomInfo.identity;
      });
    },

    receivers() {
      return this.getReceivers();
    },

    noSelectedParticipants() {
      return !this.receivers.length;
    },
  },

  methods: {
    ...mapActions('rooms', ['createMeetingToken']),
    newRoomName() {
      return this.inviteForm?.roomName || 'Interview';
    },

    createInviteMessage(identity, token, room) {
      return {
        to: { identity },
        roomName: this.newRoomName(),
        room,
        token,
      };
    },

    inviteRoute(action) {
      if (!this.currentInvite) return '';

      let url = appRoute('/rooms') + '?token=' + this.currentInvite.token;

      if (action) {
        url += '&action=' + action;
      }

      return url;
    },

    getMeetingRoomName(participants) {
      let room = [this.roomInfo.room, 'breakout'];
      participants.forEach((part) => {
        room.push(part.identity);
      });
      return room.join('-');
    },

    // invite only guests and self
    async inviteParticipants() {
      this.isLoading = true;

      try {
        if (!this.receivers.length) {
          this.isLoading = false;
          this.showInviteForm = false;
          return;
        }

        const newRoom = this.getMeetingRoomName(this.receivers);
        this.sendInviteToSelf(newRoom);

        const invitePromises = this.receivers.map((participant) => {
          return this.sendInviteToParticipant(participant, newRoom);
        });

        // Wait for all invites to be sent and first invite_accepted event fired
        await Promise.all(invitePromises);

        // send message to update assignment status / fire kept/rejected events
        this.updateAssignmentStatus(this.receivers);
      } catch (error) {
        console.error('Error sending invites:', error);
      } finally {
        this.isLoading = false;
        this.showInviteForm = false;
      }
    },

    updateAssignmentStatus(receivers) {
      const groupedReceivers = receivers.reduce((acc, p) => {
        const { identity, assignment_status } = p;
        if (assignment_status && assignment_status !== 'rejected') {
          (acc[assignment_status] ||= []).push(identity);
        }
        return acc;
      }, {});

      Object.keys(groupedReceivers).forEach((status) => {
        const remainingGuests = this.guestParticipants
          .slice()
          .filter(({ assignment_status, identity }) => {
            return (
              assignment_status === status &&
              !groupedReceivers[status].includes(identity)
            );
          })
          .sort((a, b) => {
            const orderA = a.assignment_order ?? Infinity;
            const orderB = b.assignment_order ?? Infinity;

            return orderA - orderB;
          });

        remainingGuests.slice(0, 10).forEach((guest, index) => {
          const expectedOrder = index + 1;

          if (guest.assignment_order == expectedOrder) return;

          const identity = guest.identity;

          const update = {
            identity,
            assignment_status: status,
            assignment_order: expectedOrder,
          };

          this.api.updateParticipantInfo(
            {
              ...update,
              sendOrderChangedEvent: true,
            },
            { to: identity }
          );

          this.createAssignmentOrderMessage(update, status);
        });
      });

      // await a bit before fire kept/rejected events
      setTimeout(() => {
        this.sendKeptOrRejectedEvent();
      }, 2000);
    },

    sendKeptOrRejectedEvent() {
      this.guestParticipants.forEach(({ identity }) => {
        this.api.updateParticipantInfo(
          {
            identity,
            sendKeptOrRejectedEvent: true,
          },
          { to: identity }
        );
      });
    },

    createAssignmentOrderMessage(update, from) {
      const { identity, assignment_status, assignment_order } = update;

      const getRecipient = (identity) =>
        this.guestParticipants.find((guest) => {
          return guest.identity === identity;
        });

      const getSender = (from) => {
        if (from === this.roomInfo?.identity) {
          return this.roomInfo;
        }

        return this.hostParticipants.find((host) => host.identity === from);
      };

      const recipient = getRecipient(identity);
      const sender = getSender(from);

      // Ensure recipient and sender exist to avoid undefined errors
      if (!recipient || !sender) {
        console.error('Recipient or sender not found');
        return;
      }

      const message = {
        txt: getAssignmentOrderMessage(assignment_order),
        date: new Date(),
        recipient: {
          text: recipient.name,
          value: recipient.identity,
          type: 'guest',
        },
        avatar: sender.avatar,
        name: sender.name,
        identity: assignment_status,
        type: 'host',
        assignment: {
          order: assignment_order,
          status: assignment_status,
        },
      };

      eventBus.trigger('sendAssignmentChange', message);
    },

    getReceivers() {
      let receivers = this.selectedParticipants.filter(({ identity }) => {
        return !this.isHostIdentity(identity);
      });

      if (this.selectedHosts.length) {
        receivers.push(...this.selectedHosts);
      }

      return receivers.map((part) => {
        return {
          identity: part.identity,
          type: part.type,
          assignment_status: part.assignment_status,
          assignment_order: part.assignment_order,
          name: part.name,
        };
      });
    },

    async sendInviteToSelf(room) {
      const token = await this.getMeetingToken(room, this.roomInfo);
      if (!token) return;

      let inviteData = this.createInviteMessage(
        this.roomInfo.identity,
        token,
        room
      );
      inviteData.from = this.infoForAction;

      // show modal
      this.processInvite(inviteData);
    },

    async sendInviteToParticipant(participant, room) {
      const { identity, type } = participant || {};
      const token = await this.getMeetingToken(room, participant);
      if (!token) return;

      const message = this.createInviteMessage(identity, token, room);
      this.api.send(message);

      analyticSender('sent_invite', {
        to_type: type,
        to_identity: identity.replace('host_', ''),
        to_room: this.inviteForm.room,
        to_room_name: this.newRoomName(),
      });
    },

    async getMeetingToken(room, participant) {
      const data = {
        room,
        participant,
        room_name: this.newRoomName(),
      };

      return await this.createMeetingToken(data);
    },

    selected() {
      this.showInviteForm = true;
    },

    received(invite) {
      eventBus.trigger('clearActionsStates');
      this.processInvite(invite);
    },

    onHostLeave(stopTracks = false) {
      this.participants.forEach((part) => {
        this.api.updateParticipantInfo({
          identity: part.identity,
          muted: true,
        });
      });

      if (stopTracks) {
        eventBus.trigger('stopTracks');
      }
    },

    moveGuestToNextRoom() {
      const { from, room } = this.currentInvite;

      analyticSender('accepted_invite', {
        invited_room: room,
        from_identity: from.identity,
      });

      this.api.disconnect();
      this.showCurrentInvite = false;

      // await room disconnect
      setTimeout(() => {
        window.location.href = this.inviteRoute('join');
      }, 100);
    },

    processInvite(invite) {
      if (!this.isInviteValid(invite)) return;

      this.currentInvite = invite;

      // redirect all guests to new room
      const shouldRedirect = !this.isHost;

      if (shouldRedirect) {
        this.moveGuestToNextRoom();
      } else {
        // open modal window
        this.showCurrentInvite = true;
      }
    },

    isInviteValid(invite) {
      const cachedInvite = JSON.parse(sessionStorage.getItem(this.storageKey));
      if (!invite?.cached || !cachedInvite) return true;

      const compareKeys = ['from', 'room', 'roomName'];
      return compareKeys.some((key) => {
        return !isEqual(invite[key], cachedInvite[key]);
      });
    },

    closeInviteModal() {
      sessionStorage.setItem(
        this.storageKey,
        JSON.stringify(this.currentInvite)
      );
      this.showCurrentInvite = false;
    },

    moveGuestToMainRoom(data) {
      const { participant, room, roomName } = data;
      if (roomName) {
        this.inviteForm.roomName = roomName;
      }
      this.sendInviteToParticipant(participant, room, true);
    },
  },
  mounted() {
    eventBus.on('moveGuestToMainRoom', this.moveGuestToMainRoom);
  },
};
</script>

<style>
.invite-text {
  font-size: 1.2em;
}
</style>
